import { useState } from "react";
import { useRule } from "../CreateRuleNew/RuleReducer";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import Button3 from "../../../Button/Button3";
import classes from "./RuleWizard.module.css";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";

const Days = ["Mon", "Tues", "Wed", "Thur", "Fri", "Sat", "Sun"];

const WizardStepRestrictions = ({ onNext, onBack }) => {
    const { state, dispatch } = useRule();

    const [showDaysOptions, setShowDaysOptions] = useState(false);
    const [showTimeOptions, setShowTimeOptions] = useState(false);
    const isLeftOpen = state.inputType === 5;
    const [showTimeFrameOptions, setShowTimeFrameOptions] = useState(isLeftOpen);
    const [timeframeValue, setTimeframeValue] = useState("");
    const [timeframeUnit, setTimeframeUnit] = useState(60000);
    const [startTimeRaw, setStartTimeRaw] = useState(null);
    const [endTimeRaw, setEndTimeRaw] = useState(null);

    const TIMEFRAME_UNITS = [
        { label: "Seconds", value: 1000 },
        { label: "Minutes", value: 60000 },
        { label: "Hours",   value: 3600000 },
    ];

    const toggleDay = (dayIndex) => {
        const next = state.days_of_week.includes(dayIndex)
            ? state.days_of_week.filter(d => d !== dayIndex)
            : [...state.days_of_week, dayIndex];
        dispatch({ type: "SET_EXECUTION_DAYS", payload: next });
    };

    const canProceed = !isLeftOpen || timeframeValue > 0;

    return (
        <>
            <div className={classes.contentHeader}>
                <h2 className={classes.contentTitle}>Restrictions</h2>
                <p className={classes.contentSubtitle}>Optionally restrict when this rule is active. These apply to all conditions.</p>
            </div>

            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "14px" }}>
                {state.conditions.length > 1 &&
                (!showTimeFrameOptions ? (
                     <Button
                        label="Set timeframe between conditions"
                        icon="pi pi-clock"
                        size="small"
                        severity="info"
                        rounded
                        outlined
                        style={{ alignSelf: "flex-start" }}
                        onClick={() => setShowTimeFrameOptions(true)}
                    />
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <span style={{ fontSize: "12px", color: "#6e6e6e" }}>Within:</span>
                            <InputNumber
                                value={timeframeValue === "" ? null : Number(timeframeValue)}
                                onValueChange={(e) => {
                                    const val = e.value ?? "";
                                    setTimeframeValue(val);
                                    dispatch({
                                        type: "SET_TIMEFRAME_WINDOW_MS",
                                        payload: val > 0 ? Number(val) * timeframeUnit : null,
                                    });
                                }}
                                min={1}
                                style={{ width: "80px", flexShrink: 0 }}
                                inputStyle={{ width: "80px" }}
                                placeholder=""
                            />
                            <Dropdown
                                value={timeframeUnit}
                                options={TIMEFRAME_UNITS}
                                onChange={(e) => {
                                    setTimeframeUnit(e.value);
                                    if (timeframeValue > 0) {
                                        dispatch({ type: "SET_TIMEFRAME_WINDOW_MS", payload: Number(timeframeValue) * e.value });
                                    }
                                }}
                                style={{ width: "180px", flexShrink: 0 }}
                            />
                            {!isLeftOpen && (
                                <Button
                                    icon="pi pi-times"
                                    size="small"
                                    severity="secondary"
                                    text
                                    onClick={() => {
                                        setShowTimeFrameOptions(false);
                                        setTimeframeValue("");
                                        dispatch({ type: "SET_TIMEFRAME_WINDOW_MS", payload: null });
                                    }}
                                />
                            )}
                        </div>
                        {isLeftOpen && (
                            <p style={{ margin: 0, fontSize: "12px", color: "#6e6e6e" }}>
                                This sets the length of time the lock must be left open before the rule fires.
                            </p>
                        )}
                    </div>
                ))}

                {!showDaysOptions ? (
                    <Button
                        label="Add day restrictions"
                        icon="pi pi-calendar-plus"
                        size="small"
                        severity="info"
                        rounded
                        outlined
                        style={{ alignSelf: "flex-start" }}
                        onClick={() => setShowDaysOptions(true)}
                    />
                ) : (
                    <div>
                        <p style={{ color: "#6e6e6e", fontSize: "13px", margin: "0 0 6px 0" }}>Active on:</p>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                            {["0", "1", "2", "3", "4", "5", "6"].map((d, i) => (
                                <Button3
                                    key={i}
                                    style={{
                                        minWidth: "50px", minHeight: "30px", fontSize: "12px",
                                        backgroundColor: state.days_of_week.includes(d) ? "var(--Primary-Color)" : undefined,
                                        color: state.days_of_week.includes(d) ? "white" : undefined,
                                    }}
                                    onClick={() => toggleDay(d)}
                                >
                                    {Days[i]}
                                </Button3>
                            ))}
                            <Button
                                icon="pi pi-times"
                                size="small"
                                severity="secondary"
                                text
                                onClick={() => {
                                    setShowDaysOptions(false);
                                    dispatch({ type: "SET_EXECUTION_DAYS", payload: ["0", "1", "2", "3", "4", "5", "6"] });
                                }}
                            />
                        </div>
                    </div>
                )}

                {!showTimeOptions ? (
                    <Button
                        label="Add time restrictions"
                        icon="pi pi-plus"
                        size="small"
                        severity="info"
                        rounded
                        outlined
                        style={{ alignSelf: "flex-start" }}
                        onClick={() => setShowTimeOptions(true)}
                    />
                ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <span style={{ fontSize: "13px", color: "#6e6e6e" }}>Between</span>
                        <Calendar
                            value={startTimeRaw}
                            onChange={(e) => {
                                setStartTimeRaw(e.value);
                                const d = e.value;
                                dispatch({
                                    type: "SET_TIME_WINDOW_START",
                                    payload: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:00`,
                                });
                            }}
                            timeOnly
                        />
                        <span style={{ fontSize: "13px", color: "#6e6e6e" }}>and</span>
                        <Calendar
                            value={endTimeRaw}
                            onChange={(e) => {
                                setEndTimeRaw(e.value);
                                const d = e.value;
                                dispatch({
                                    type: "SET_TIME_WINDOW_END",
                                    payload: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:00`,
                                });
                            }}
                            timeOnly
                        />
                        <Button
                            icon="pi pi-times"
                            size="small"
                            severity="secondary"
                            text
                            onClick={() => {
                                setShowTimeOptions(false);
                                setStartTimeRaw(null);
                                setEndTimeRaw(null);
                                dispatch({ type: "SET_TIME_WINDOW_START", payload: null });
                                dispatch({ type: "SET_TIME_WINDOW_END", payload: null });
                            }}
                        />
                    </div>
                )}
            </div>

            <div className={classes.navRow}>
                <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={onBack} />
                <Button
                    label="Next"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    severity="success"
                    disabled={!canProceed}
                    onClick={onNext}
                />
            </div>
        </>
    );
};

export default WizardStepRestrictions;
